import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { TRANSLATIONS, LANGUAGES } from "@/lib/i18n";

const LanguageContext = createContext(null);

// Storage keys
const LS_LANG = "xaluca_lang";             // current language (any source)
const LS_MANUAL = "xaluca_lang_manual";    // "1" once the user has manually
                                            //  selected via the on-page switcher.
                                            //  Persisted across reloads so we
                                            //  never overwrite an explicit
                                            //  override with the auto-detect.
const SUPPORTED = new Set(["fr", "en", "es"]);
const FALLBACK = "fr";

// Resolves the *initial* render language synchronously, before any
// network call resolves. Priority:
//   1. Manual override from a previous session (localStorage flag set)
//   2. IP-detected language stashed by the boot script in index.html
//      (cold cache: piggybacked on /api/text_slots; warm cache: tiny
//      /api/i18n/detect call kicked off at boot)
//   3. Last-known language from localStorage (warm-cache returning
//      visitor whose detect fetch hasn't resolved yet — using the
//      previously-stored detected value avoids a flicker)
//   4. Historical fallback ("fr")
const resolveInitialLang = () => {
  if (typeof window === "undefined") return FALLBACK;
  let stored = null;
  try {
    if (localStorage.getItem(LS_MANUAL) === "1") {
      const s = localStorage.getItem(LS_LANG);
      if (s && SUPPORTED.has(s)) return s;
    }
    stored = localStorage.getItem(LS_LANG);
  } catch { /* private mode */ }
  const detected = (typeof window !== "undefined" && window.__CMS_DETECTED_LANG__) || null;
  if (detected && SUPPORTED.has(detected)) return detected;
  if (stored && SUPPORTED.has(stored)) return stored;
  return FALLBACK;
};

export const LanguageProvider = ({ children }) => {
  const [lang, _setLang] = useState(resolveInitialLang);

  // Mirror the active language into the <html lang="…"> attribute and
  // (for non-manual cases) into localStorage so subsequent visits get
  // the same language without a re-detect round-trip.
  useEffect(() => {
    if (typeof document !== "undefined") {
      // Fallback to "en" if lang somehow arrives empty/undefined so we
      // never leave <html lang=""> in the DOM (breaks SEO + a11y).
      document.documentElement.lang = lang || "en";
    }
    try {
      window.localStorage.setItem(LS_LANG, lang);
    } catch { /* private mode */ }
  }, [lang]);

  // The boot script kicks off TWO promises that can carry a detected
  // language: `__CMS_INITIAL_FETCH__` (cold cache, piggybacks on the
  // text-slots payload) and `__CMS_LANG_DETECT_FETCH__` (every visit,
  // tiny standalone detect call). Whichever resolves first wins; both
  // are honoured. We swap to the detected language ONLY when the user
  // has not manually overridden via the selector.
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      if (window.localStorage.getItem(LS_MANUAL) === "1") return;
    } catch { /* noop */ }
    let cancelled = false;
    const adopt = (detected) => {
      if (cancelled) return;
      if (!detected || !SUPPORTED.has(detected)) return;
      try {
        if (window.localStorage.getItem(LS_MANUAL) === "1") return;
      } catch { /* noop */ }
      _setLang((current) => (current === detected ? current : detected));
    };
    const langPromise = window.__CMS_LANG_DETECT_FETCH__;
    if (langPromise && typeof langPromise.then === "function") {
      langPromise.then((json) => adopt((json && json.language) || window.__CMS_DETECTED_LANG__)).catch(() => {});
    }
    const fetchPromise = window.__CMS_INITIAL_FETCH__;
    if (fetchPromise && typeof fetchPromise.then === "function") {
      fetchPromise.then((json) => adopt((json && json.detected_lang) || window.__CMS_DETECTED_LANG__)).catch(() => {});
    }
    return () => { cancelled = true; };
  }, []);

  // The only setter exposed to the UI. ANY call to this counts as a
  // manual override — exactly what the user asked for: "users must
  // always be able to manually switch languages at any time". The
  // override flag persists across sessions so we never re-impose an
  // auto-detected language on someone who has already picked one.
  const setLang = useCallback((next) => {
    if (!next || !SUPPORTED.has(next)) return;
    try {
      window.localStorage.setItem(LS_MANUAL, "1");
      window.localStorage.setItem(LS_LANG, next);
    } catch { /* noop */ }
    _setLang(next);
  }, []);

  // Escape hatch in case the user wants to go back to auto-detect.
  // Not currently surfaced in the UI but available to admin tooling.
  const clearManualOverride = useCallback(() => {
    try {
      window.localStorage.removeItem(LS_MANUAL);
    } catch { /* noop */ }
    const detected = window.__CMS_DETECTED_LANG__ || null;
    if (detected && SUPPORTED.has(detected)) _setLang(detected);
  }, []);

  const value = useMemo(() => {
    const t = (path) => {
      const segs = path.split(".");
      let cur = TRANSLATIONS[lang] || TRANSLATIONS.fr;
      for (const s of segs) {
        if (cur && typeof cur === "object" && s in cur) cur = cur[s];
        else return path;
      }
      return cur;
    };
    return { lang, setLang, clearManualOverride, t, languages: LANGUAGES };
  }, [lang, setLang, clearManualOverride]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useLanguage = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLanguage must be used inside LanguageProvider");
  return ctx;
};
